import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { post, get } from '@chat/utils';
import useUploadChunk from './useUploadChunk';
import {
    CHUNK,
    CHUNK_END,
    CHUNK_START,
    IM_SEND,
    IM_VIDEO_AUTH,
    IM_VIDEO_RECORD,
    IM_VIDEO_URL
} from '@/api/url';
import CircleProgress from './CircleProgress';
import { setLastTime, setVideoLock, setVideoParam } from '@/redux/im/action';
import VideoDefault from '@/assets/images/video-default.jpg';
import { IM_MESSAGE_VIDEO } from '@/typings/global';

function MsgVideo({ message }: { message: File | IM_MESSAGE_VIDEO }): JSX.Element {
    const dispatch = useDispatch();
    const { prefix, video_lock, STATUS, last_time } = useSelector(
        ({ IMReducer }) => ({
            prefix: IMReducer.prefix,
            video_lock: IMReducer.video_lock,
            STATUS: IMReducer.STATUS,
            last_time: IMReducer.last_time
        }),
        shallowEqual
    );

    const [auth, setAuth] = useState(null);

    const onException = (_type, _msg) => {
        if (_type === 'error') console.error(_msg);
        if (_type === 'warning') IM.Toast.error({ content: _msg });
    };

    const getAuth = async (): Promise<any> => {
        const res = await get(IM_VIDEO_AUTH(prefix));
        const data = res.data.d;
        setAuth({
            token: data.token,
            now: data.now,
            uid: data.auth
        });
    };

    const onSuccess = async (_file) => {
        const data = { c: 'video', m: `[video=${_file.uuid};${_file.name};${auth.uid}]` };
        await post(IM_SEND(prefix), data);
    };

    const onOpen = (_file) => {
        if (!_file?.urls?.url) return;
        dispatch(setVideoParam(_file));
        IM.handlePopup({ popup: 'video', open: true });
        // IM.SDKServer.openNewWindow(url, '视频播放', '/pages/commonUrl/index');
    };

    const onTimeout = async (now) => {
        // dispatch(setLastTime({ last_time, compare: 'delay', now }));
        await post(IM_VIDEO_RECORD(prefix));
    };

    const onStatusChange = async ({ status }) => {
        if (status === 'progress') {
            dispatch(setVideoLock(true));
        } else {
            dispatch(setVideoLock(false));
        }
    };

    const { remove, reUpload, open, fileList, cancel } = useUploadChunk({
        auth,
        initFile: message instanceof File ? message : undefined,
        value: message instanceof File ? [] : [{ ...message, status: 'complete' }],
        action: CHUNK,
        actionStart: CHUNK_START,
        actionEnd: CHUNK_END,
        actionUpdate: IM_VIDEO_URL(prefix),
        extData: {},
        onException,
        onOpen,
        onSuccess,
        onStatusChange,
        onTimeout,
        STATUS
    });

    const onReUpload = (_file) => {
        const isOutline = IM.IMServer.checkSessionBeforeSend(1);
        if (isOutline) {
            return;
        }
        if (video_lock) {
            IM.Toast.error({ content: '已有视频上传中，请稍后再试' });
            return;
        }
        remove(_file);
        reUpload();
    };

    useEffect(() => {
        if (message instanceof File) {
            getAuth();
        }
    }, [message]);
    const file = fileList[0];
    return (
        <div className="im-message-content im-message-video">
            {file?.status === 'progress' && (
                <div className="cover">
                    <span className="mask" />
                    <span className="overlap">
                        <CircleProgress className="progress" percent={file.percent} />
                    </span>
                    <span className="cancel" onClick={() => cancel(file)} />
                </div>
            )}
            {['complete', 'wait', 'transcoding', 'open'].includes(file?.status) && (
                <div className="cover" onClick={() => open(file)} style={{ cursor: 'pointer' }}>
                    <img className="img" src={file.screenshot || VideoDefault} alt="" />
                    <span className="mask" />
                    <span className="overlap">
                        <span className="icon" />
                    </span>
                </div>
            )}
            {['error', 'cancelled'].includes(file?.status) && (
                <div className="cover">
                    <span className="mask" />
                    <span className="overlap" style={{ cursor: 'pointer' }}>
                        <span
                            className="reupload"
                            onClick={() => {
                                onReUpload(file);
                            }}
                        />
                        <span className="error-text">上传失败，请重新上传</span>
                    </span>
                    <span className="error-icon" />
                </div>
            )}
        </div>
    );
}

export default MsgVideo;
